import { ChangeEvent, FC, useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Flex,
    Input,
    Select,
    Text,
    VStack,
    useToast,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
} from '@chakra-ui/react';

import { keyBy, Dictionary } from 'lodash';

import {
    DEFAULT_USER_GROUP,
    DISABLED_FIELDS,
    HEADER_USER,
    SELECT_LANGUAGES,
    THEADER_USER,
    USER_FIELDS,
    USER_FORM_SKIPPED_FIELD,
} from 'pages/UserManagement/const';
import {
    ICompanyGroups,
    IInternationalCompany,
    ISelectProps,
    IUserGroup,
    TOptionalUser,
    TUser,
    TUSER,
} from 'pages/UserManagement/types';
import { entries, intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import {
    useAddUserMutation,
    useGetCompanyGroupQuery,
    useGetInternationalCompanyQuery,
    useGetUsersGroupQuery,
    useResetUserPasswordMutation,
    useUpdateUserMutation,
} from 'core/API/usersApi';

import { isEmail } from 'utils/strings';
import AMultiSelect, { MultiSelectOption } from 'components/atoms/AMultiSelect';
import { isFetchBaseQueryError } from 'utils/erros';
import { linkStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';
import { useLazyGetCompanyGroupsQuery } from 'core/API/internationalCompanyGroupApi';
import { IinternationalCompanies } from 'types';

type TGenericItem = {
    id: number;
    name: string;
    [key: string]: any;
};

const MUserEdit: FC<{
    isInternational: boolean;
    isAdd: boolean;
    values?: TUser;
    isInternationalUser?: boolean;
    internationalCompanyGroups: TGenericItem[];
    internationalCompanies: IinternationalCompanies[];
    onClose: () => void;
}> = ({
    isInternational,
    isAdd,
    values,
    isInternationalUser,
    internationalCompanyGroups,
    internationalCompanies,
    onClose,
}) => {
        const toast = useToast();
        const translate = useTranslation();
        const [updateUserMutation, { isLoading }] = useUpdateUserMutation();
        const [addUserMutation, { isLoading: isAddUserLoading }] = useAddUserMutation();
        const [resetPasswordMutation, { isLoading: isResetPasswordLoading }] = useResetUserPasswordMutation();
        const [internationalComapnyById, setInternationalCompanyById] = useState<Dictionary<IinternationalCompanies>>({});
        const [companyList, setCompanyList] = useState<TGenericItem[]>([]);
        const [getIntCompanysCompanyGroups, { data: companyGroupsData, isError }] = useLazyGetCompanyGroupsQuery();

        const {
            data: userGroupsData,
            isLoading: isGetUserGroupLoading,
            isError: isGetUserGroupError,
        } = useGetUsersGroupQuery();

        const {
            data: userCompanyGroupsData,
            isLoading: isGetCompanyGroupLoading,
            isError: isGetCompanyGroupError,
        } = useGetCompanyGroupQuery();

        const {
            data: userInternationalCompanyData,
            isLoading: isGetInternationalCompanyLoading,
            isError: isGetInternationalCompanyError,
        } = useGetInternationalCompanyQuery();

        const { isOpen: isResetPwdModalOpen, onOpen: onOpenResetPwdModal, onClose: onCloseResetPwdModal } = useDisclosure();
        const finalRef = useRef(null);
        const [userGroups, setUserGroupValues] = useState<IUserGroup[]>([]);
        const [companyGroups, setCompanyGroupValues] = useState<ICompanyGroups[]>([]);
        const [IntUserCompanyGroups, setIntUserCompanyGroups] = useState<TGenericItem[]>([]);
        const [internationCompany, setInternationCompany] = useState<IInternationalCompany[]>([]);
        const [fieldsValues, setFieldsValues] = useState<TUser>(values as TUser);
        const [initialFieldsValues, setInitialFieldsValues] = useState<TOptionalUser>(values as TOptionalUser);
        const [isUsernameValid, setIsUsernameValid] = useState<boolean>(false);
        const [isUserGroupValid, setIsUserGroupValid] = useState<boolean>(false);
        const [hiddenInput, setHiddenInput] = useState<string[]>(USER_FORM_SKIPPED_FIELD);
        const [selectedTools, setSelectedTools] = useState<MultiSelectOption[]>([]);
        const [options, setOptions] = useState<MultiSelectOption[]>([]);

        useEffect(() => {
            if (!isAdd && values) {
                setIsUsernameValid(isEmail(values?.username) !== null);
                setFieldsValues(values);
                setInitialFieldsValues(values);
            }

            if (isAdd) {
                const userGroup: IUserGroup = DEFAULT_USER_GROUP;

                setFieldsValues({
                    ...fieldsValues,
                    is_active: true,
                    groups: [userGroup],
                });

                setSelectedTools([userGroup]);
            }
        }, [values]);

        useEffect(() => {
            if (fieldsValues?.groups?.length) {
                setIsUserGroupValid(true);
            } else {
                setIsUserGroupValid(false);
            }
        }, [fieldsValues]);

        const updateCompanyGroups = async () => {
            const cg = (await getIntCompanysCompanyGroups(Number(fieldsValues.international_company))) as TCompanyGroups;
            type TCompanyGroups = {
                error?: any;
                isError?: boolean;
                data?: {
                    data: {
                        companyGroups: {
                            company_id: number;
                            company_name: string;
                            id: number;
                            int_company_groups_ids: number[];
                            name: string;
                        }[];
                    };
                };
            };

            if (cg?.data?.data?.companyGroups) {
                const companyGroupsList = cg?.data?.data?.companyGroups;
                const filteredCompanyGroups = companyGroupsList.filter(
                    (companyGroup) => companyGroup.company_id === Number(fieldsValues.company),
                ) as TGenericItem[];

                setIntUserCompanyGroups(filteredCompanyGroups);
            }
        };

        useEffect(() => {
            updateCompanyGroups();
        }, [fieldsValues.international_company, fieldsValues.company]);

        useEffect(() => {
            if (!isGetUserGroupLoading && !isGetUserGroupError && userGroupsData) {
                setUserGroupValues(userGroupsData.groups);
                setOptions(
                    userGroupsData.groups.map((item) => {
                        return { id: item.id, name: item.name };
                    }),
                );

                if (!isAdd) {
                    const selectedToolsData = userGroups
                        .filter((tool) => values && (values.groups as IUserGroup[]).find((group) => group.id === tool.id))
                        .map((tool) => tool);

                    setSelectedTools(selectedToolsData);
                }
            }
        }, [isGetUserGroupLoading, isGetUserGroupError, userGroupsData, userGroups]);

        useEffect(() => {
            setInternationalCompanyById(keyBy(internationalCompanies, 'id'));
        }, [internationalCompanies]);

        useEffect(() => {
            if (fieldsValues.international_company && internationalComapnyById[fieldsValues.international_company]) {
                setCompanyList(internationalComapnyById[fieldsValues.international_company].companies);
            }
        }, [fieldsValues.international_company, internationalComapnyById]);

        useEffect(() => {
            if (userCompanyGroupsData && !isGetCompanyGroupLoading && !isGetCompanyGroupError) {
                setCompanyGroupValues(userCompanyGroupsData.companyGroup);
            }
        }, [isGetCompanyGroupLoading, isGetCompanyGroupError]);

        useEffect(() => {
            if (userInternationalCompanyData && !isGetInternationalCompanyError && !isGetInternationalCompanyLoading) {
                setInternationCompany(userInternationalCompanyData.internationalCompanies);
            }
            if (!userInternationalCompanyData && !isGetInternationalCompanyLoading && isGetInternationalCompanyError) {
                setHiddenInput((prev) => [...prev, 'international_company']);
            }
        }, [isGetInternationalCompanyError, isGetInternationalCompanyLoading]);

        useEffect(() => {
            if (!isInternationalUser) {
                setHiddenInput((prev) => [...prev, 'company', 'international_company_group']);
            }
        }, [isInternationalUser]);

        useEffect(() => {
            if (isInternationalUser && isAdd) {
                setHiddenInput((prev) => [...prev, 'international_company_group']);
            }
        }, [isAdd]);

        const onChangeMultiSelect = (selected: MultiSelectOption[]) => {
            setFieldsValues({
                ...fieldsValues,
                groups: [...(selected as IUserGroup[])],
            });
        };

        const handleChangeInput = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: TUSER) => {
            if (event.target.value !== null) {
                if (field === 'company_group') {
                    setFieldsValues({ ...fieldsValues, [field]: Number(event.target.value) });
                } else {
                    setFieldsValues({ ...fieldsValues, [field]: event.target.value });
                }

                if (field === 'username') {
                    setIsUsernameValid(isEmail(event.target.value) !== null);
                }
            }
        };

        const handleCheckRow = (e: ChangeEvent<HTMLInputElement>, isActive: boolean) => {
            setFieldsValues({ ...fieldsValues, is_active: !isActive });
        };

        const renderSelectInput = <T extends ISelectProps>(
            field: keyof typeof HEADER_USER,
            data: T[],
            value: any,
            isDisabled = false,
        ) => {
            return (
                <Flex w="100%" justifyContent="space-between" key={field}>
                    <Text mr="24px">{translate(HEADER_USER[field])}</Text>
                    <Select
                        value={value}
                        onChange={(e) => handleChangeInput(e, field)}
                        placeholder={
                            isDisabled ? ' ' : `${translate('users.edit.select')} ${translate(HEADER_USER[field])}`
                        }
                        w="60%"
                        bg="white"
                        isDisabled={isDisabled}
                    >
                        {data && data.map((group) => <option value={group.id}>{group.name}</option>)}
                    </Select>
                </Flex>
            );
        };

        const renderSelectInputUserGroup = <T extends ISelectProps>(
            field: keyof typeof HEADER_USER,
            data: T[],
            value: IUserGroup[],
        ) => {
            return (
                <Flex w="100%" justifyContent="space-between" key={field}>
                    <Text mr="24px">{translate(HEADER_USER[field])}*</Text>
                    <VStack w="60%">
                        <AMultiSelect selectedOptions={selectedTools} options={options} onChange={onChangeMultiSelect} />
                        {!isUserGroupValid && data.length && (
                            <Text fontSize="sm">{translate('users.edit.error.user_group')}</Text>
                        )}
                    </VStack>
                </Flex>
            );
        };

        const renderInput = (field: THEADER_USER, fieldsValuesInput: TUser, isDisabled = false) => {
            let data: any[] = [];

            switch (field) {
                case 'language':
                    data = SELECT_LANGUAGES;
                    break;
                case 'international_company_group':
                    data = internationalCompanyGroups;
                    break;
                case 'groups':
                    data = userGroups;
                    if (fieldsValuesInput) {
                        return renderSelectInputUserGroup(field, data, fieldsValuesInput.groups as IUserGroup[]);
                    }
                    break;
                case 'company':
                    data = companyList;
                    break;
                case 'company_group':
                    data = isInternationalUser ? IntUserCompanyGroups : companyGroups;
                    break;
                case 'international_company':
                    data = isInternationalUser ? internationalCompanies : internationCompany;
                    break;
                case 'has_notification_pdf_active':
                case 'is_active':
                case 'is_staff':
                case 'is_superuser':
                    return (
                        <Flex w="100%" justifyContent="space-between" key={field}>
                            <Text mr="24px">{translate(HEADER_USER[field])}</Text>
                            <Checkbox
                                onChange={(e) => handleCheckRow(e, fieldsValuesInput[field])}
                                isChecked={fieldsValuesInput ? fieldsValuesInput[field] : false}
                            />
                        </Flex>
                    );
                case 'username':
                    return (
                        <Flex w="100%" justifyContent="space-between" key={field}>
                            <Text mr="24px">{translate(HEADER_USER[field])}*</Text>
                            <VStack w="60%">
                                <Input
                                    disabled={!isAdd}
                                    isInvalid={!isUsernameValid}
                                    errorBorderColor="crimson"
                                    onChange={(e) => handleChangeInput(e, field)}
                                    bg="white"
                                    value={fieldsValuesInput ? fieldsValuesInput[field] : ''}
                                />
                                {!isUsernameValid && fieldsValuesInput[field] && (
                                    <Text fontSize="sm">{translate('users.edit.error.email')}</Text>
                                )}
                            </VStack>
                        </Flex>
                    );
                default:
                    return (
                        <Flex w="100%" justifyContent="space-between" key={field}>
                            <Text mr="24px">{translate(HEADER_USER[field])}</Text>
                            <Input
                                onChange={(e) => handleChangeInput(e, field)}
                                w="60%"
                                bg="white"
                                value={fieldsValuesInput ? fieldsValuesInput[field] : ''}
                            />
                        </Flex>
                    );
            }

            return renderSelectInput(field, data, fieldsValuesInput ? fieldsValuesInput[field] : '', isDisabled);
        };

        const openResetPwdModal = () => {
            onOpenResetPwdModal();
        };

        const resetPasswordTrigger = async () => {
            try {
                const res = await resetPasswordMutation([fieldsValues.id]);
                if ('error' in res) {
                    const errorObj = res.error;
                    if (isFetchBaseQueryError(errorObj)) {
                        const errorMsg: string =
                            'message' in (errorObj.data as any)
                                ? (errorObj.data as any).message
                                : JSON.stringify(errorObj.data);
                        throw new Error(errorMsg);
                    }
                    throw new Error('Error reset password');
                }

                onClose();
                onCloseResetPwdModal();

                toast({
                    title: `Succesfully reset password`,
                    status: 'success',
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const renderConfirmResetPasswordModal = () => {
            return (
                <Modal finalFocusRef={finalRef} isOpen={isResetPwdModalOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>{translate('users.edit.modal.reset_password.title')}</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Text fontWeight="bold" mb="1rem">
                                {translate('users.edit.modal.reset_password.body')} {fieldsValues.username}
                            </Text>
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                onClick={resetPasswordTrigger}
                                colorScheme="red"
                                variant="solid"
                                mr={3}
                                isLoading={isResetPasswordLoading}
                            >
                                {translate('users.edit.modal.reset_password.ok')}
                            </Button>
                            <Button colorScheme="blue" mr={3} onClick={onClose} isLoading={isResetPasswordLoading}>
                                {translate('users.edit.modal.cancel')}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            );
        };

        const renderUserForm = () => {
            const form = USER_FIELDS.filter((field) => !hiddenInput.includes(field)).map((field) => {
                return renderInput(field as THEADER_USER, fieldsValues, DISABLED_FIELDS.includes(field));
            });

            if (!isAdd) {
                form.push(
                    <Flex w="100%" justifyContent="end">
                        <Box {...linkStyle} onClick={openResetPwdModal} color="blue.500">
                            {translate('users.edit.modal.reset_password.title')}
                        </Box>
                        {renderConfirmResetPasswordModal()}
                    </Flex>,
                );
            }

            return form;
        };

        const createUpdateUserObject = (fieldsValuesCreate: TOptionalUser) => {
            const newFielValues: TOptionalUser = {
                ...fieldsValuesCreate,
                groups: fieldsValuesCreate.groups && (fieldsValuesCreate.groups as IUserGroup[]).map((group) => group.id),
            };

            if (!isInternational) {
                delete newFielValues.international_company;
            }
            return newFielValues;
        };

        const createUserData = (fieldsValuesCreate: TOptionalUser) => {
            const newFieldsValue: TOptionalUser = {
                ...fieldsValuesCreate,
                groups:
                    fieldsValuesCreate.groups && fieldsValuesCreate.groups.map((userGroup) => (userGroup as IUserGroup).id),
            };

            entries(newFieldsValue).forEach(([key, value]) => {
                switch (key) {
                    case 'company_group':
                        if (newFieldsValue.company_group === 0) {
                            delete newFieldsValue[key];
                        }
                        break;
                    case 'groups':
                        if (
                            newFieldsValue.groups &&
                            (newFieldsValue.groups[0] === 0 ||
                                !newFieldsValue.groups[0] ||
                                newFieldsValue.groups.length === 0)
                        ) {
                            delete newFieldsValue[key];
                        }
                        break;
                    default:
                        if (value === '') {
                            delete newFieldsValue[key];
                        }
                        break;
                }
            });

            return newFieldsValue;
        };

        const handleUpdateUser = async () => {
            try {
                if (fieldsValues) {
                    const fieldsToSubmit = entries(fieldsValues).reduce((acc: any, [key, value]) => {
                        if (initialFieldsValues[key as keyof TOptionalUser] !== value) {
                            acc[key] = value;
                        }
                        return acc;
                    }, {} as TOptionalUser);

                    const res = isAdd
                        ? await addUserMutation(createUserData(fieldsToSubmit))
                        : await updateUserMutation({
                            id: String(fieldsValues.id),
                            data: createUpdateUserObject(fieldsToSubmit),
                        });

                    if ('error' in res) {
                        const errorObj = res.error;
                        if (isFetchBaseQueryError(errorObj)) {
                            const errorMsg: string =
                                'message' in (errorObj.data as any)
                                    ? (errorObj.data as any).message
                                    : JSON.stringify(errorObj.data);
                            throw new Error(errorMsg);
                        }
                        throw new Error('error.generic');
                    }

                    if (res?.data?.users[0]?.failed_users?.length > 0) {
                        throw new Error(res?.data?.users[0]?.failed_users[0]?.error);
                    }
                }

                toast({
                    title: isAdd ? translate('user.add.message.success') : translate('user.edit.message.success'),
                    status: 'success',
                    isClosable: true,
                });

                onClose();
            } catch (e) {
                toast({
                    title: `${e}`,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        return (
            <Box>
                <Flex w="100%" alignItems="flex-start" flexDir="column" px="16px">
                    <VStack w="100%" alignItems="flex-start">
                        {renderUserForm()}
                    </VStack>
                </Flex>
                <Flex marginTop={8} flex="1" justifyContent="flex-end">
                    <Button onClick={onClose} size="sm" variant="outline" mr={3}>
                        {translate('users.edit.modal.cancel')}
                    </Button>
                    <Button
                        isDisabled={!isUsernameValid || !isUserGroupValid}
                        onClick={intentionalyFloatingPromiseReturn(handleUpdateUser)}
                        size="sm"
                        colorScheme="blue"
                        bg="blueMain"
                        isLoading={isLoading || isAddUserLoading || isResetPasswordLoading}
                    >
                        {translate('users.edit.modal.apply')}
                    </Button>
                </Flex>
            </Box>
        );
    };

export default MUserEdit;
